/**
 * KioskLauncherCard — Admin Control Panel widget for starting / resuming kiosk sessions.
 *
 * Renders a compact card that lets an admin:
 *   1. Type a new event name + (optional) location, then click "Start Session"
 *   2. OR pick a previous session from the dropdown to auto-fill those fields
 *   3. OR click "Open Active Session" if one is already running, to jump straight to /kiosk
 *
 * On a successful start (or when there's already an active session), the admin is
 * navigated to /kiosk in the same tab so the iPad immediately shows the welcome screen.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tablet, ChevronDown, Loader2, Mail, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import api from "../../../lib/api";

const ADMIN_TEST_EMAIL = "Admin@NewAudia.com";
const TEST_EMAIL_RESULT_TTL_MS = 10000;

const KioskLauncherCard = () => {
  const navigate = useNavigate();
  const [eventName, setEventName] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [selectedPrevId, setSelectedPrevId] = useState("");
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Test-email diagnostic state
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);  // { success, transport, detail, error }
  const testResultTimerRef = useRef(null);

  // Load past + active sessions on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [statusRes, sessRes] = await Promise.all([
          api.get("/kiosk/session-status"),
          api.get("/kiosk/sessions?limit=25"),
        ]);
        if (cancelled) return;
        setActiveSession(statusRes.data?.active_session || null);
        setSessions(sessRes.data || []);
      } catch (err) {
        if (!cancelled) setError("Unable to load kiosk session history.");
      } finally {
        if (!cancelled) setLoadingSessions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePrevPick = (id) => {
    setSelectedPrevId(id);
    if (!id) return;
    const match = sessions.find((s) => s.session_id === id);
    if (match) {
      setEventName(match.event_name || "");
      setEventLocation(match.event_location || "");
    }
  };

  const handleStart = async () => {
    if (!eventName.trim()) {
      setError("Event name is required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post("/kiosk/start-session", {
        event_name: eventName.trim(),
        event_location: eventLocation.trim() || null,
      });
      navigate("/kiosk");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to start session.");
      setSubmitting(false);
    }
  };

  const handleOpenActive = () => navigate("/kiosk");

  // ---- Test-email diagnostic ----
  // Cleanup timer on unmount so we don't try to setState after navigation away.
  useEffect(() => {
    return () => {
      if (testResultTimerRef.current) clearTimeout(testResultTimerRef.current);
    };
  }, []);

  const handleSendTestEmail = async () => {
    // If a previous result is still showing its 10s window, cancel its timer
    // so the new result replaces it cleanly.
    if (testResultTimerRef.current) {
      clearTimeout(testResultTimerRef.current);
      testResultTimerRef.current = null;
    }
    setTestSending(true);
    setTestResult(null);
    try {
      const res = await api.post("/kiosk/test-email", { to_email: ADMIN_TEST_EMAIL });
      const data = res.data || {};
      // Backend returns: { success, detail, smtp_server, smtp_port }.
      // The detail string for success references the transport via diagnostic;
      // we surface the configured server:port pair so the operator sees what
      // was used when the send succeeded.
      const transport = data.smtp_server && data.smtp_port
        ? `${data.smtp_server}:${data.smtp_port}`
        : "configured SMTP";
      setTestResult({
        success: !!data.success,
        transport,
        detail: data.detail || "",
        error: data.success ? null : (data.detail || "Unknown failure"),
      });
    } catch (err) {
      const status = err?.response?.status;
      let errMsg;
      if (!err?.response) {
        errMsg = "Network error — check connection";
      } else if (status === 500) {
        errMsg = err.response.data?.detail
          || "Server error — see /api/kiosk/email-diagnostic";
      } else {
        errMsg = err.response.data?.detail || err.message || "Request failed";
      }
      setTestResult({ success: false, transport: null, detail: "", error: errMsg });
    } finally {
      setTestSending(false);
      // Auto-clear after 10s
      testResultTimerRef.current = setTimeout(() => {
        setTestResult(null);
        testResultTimerRef.current = null;
      }, TEST_EMAIL_RESULT_TTL_MS);
    }
  };

  return (
    <Card className="bg-slate-900 border-slate-700" data-testid="kiosk-launcher-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-900/40">
              <Tablet className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Event Kiosk</h2>
              <p className="text-sm text-slate-400">
                Start or resume an in-person lead-capture session
              </p>
            </div>
          </div>
        </div>

        {/* Active-session shortcut */}
        {activeSession && (
          <div
            className="mb-4 rounded-lg border border-teal-700/60 bg-teal-900/20 p-4 flex items-center justify-between gap-3"
            data-testid="kiosk-active-session-banner"
          >
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-teal-300 font-semibold">
                Active session
              </div>
              <div className="text-white font-semibold truncate">
                {activeSession.event_name}
                {activeSession.event_location ? ` — ${activeSession.event_location}` : ""}
              </div>
              <div className="text-xs text-slate-400">
                {activeSession.lead_count} lead{activeSession.lead_count === 1 ? "" : "s"} captured
              </div>
            </div>
            <Button
              onClick={handleOpenActive}
              data-testid="kiosk-open-active-btn"
              className="bg-teal-600 hover:bg-teal-500 text-white shrink-0"
            >
              Open Kiosk
            </Button>
          </div>
        )}

        {/* Previous-event picker */}
        <div className="mb-4">
          <label
            htmlFor="kiosk-prev-session"
            className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2"
          >
            Resume from a previous event (optional)
          </label>
          <div className="relative">
            <select
              id="kiosk-prev-session"
              value={selectedPrevId}
              onChange={(e) => handlePrevPick(e.target.value)}
              disabled={loadingSessions || submitting}
              data-testid="kiosk-prev-session-select"
              className="w-full appearance-none bg-slate-800 border border-slate-600 text-white text-sm rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:border-teal-500"
            >
              <option value="">— New event —</option>
              {sessions.map((s) => {
                const dt = s.started_at ? new Date(s.started_at).toLocaleDateString() : "";
                const tag = s.status === "active" ? "  (active)" : "";
                return (
                  <option key={s.session_id} value={s.session_id}>
                    {s.event_name}
                    {s.event_location ? ` — ${s.event_location}` : ""}
                    {dt ? ` · ${dt}` : ""}
                    {tag}
                  </option>
                );
              })}
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            {loadingSessions && (
              <Loader2 className="w-4 h-4 text-slate-400 absolute right-9 top-1/2 -translate-y-1/2 animate-spin" />
            )}
          </div>
        </div>

        {/* Event name + location */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <label
              htmlFor="kiosk-event-name"
              className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2"
            >
              Event name *
            </label>
            <input
              id="kiosk-event-name"
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder="Pitch Competition — Colorado Springs"
              maxLength={200}
              data-testid="kiosk-launcher-event-name-input"
              className="w-full bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-500"
            />
          </div>
          <div>
            <label
              htmlFor="kiosk-event-location"
              className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2"
            >
              Location (optional)
            </label>
            <input
              id="kiosk-event-location"
              type="text"
              value={eventLocation}
              onChange={(e) => setEventLocation(e.target.value)}
              placeholder="Venue or city"
              maxLength={200}
              data-testid="kiosk-launcher-event-location-input"
              className="w-full bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-500"
            />
          </div>
        </div>

        {error && (
          <div
            className="mb-3 text-sm text-red-300 bg-red-900/30 border border-red-700/50 rounded-lg px-3 py-2"
            data-testid="kiosk-launcher-error"
          >
            {error}
          </div>
        )}

        <Button
          onClick={handleStart}
          disabled={submitting || !eventName.trim()}
          data-testid="kiosk-launcher-start-btn"
          className="w-full bg-teal-600 hover:bg-teal-500 text-white font-semibold"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Starting…
            </>
          ) : selectedPrevId ? (
            "Restart Session & Open Kiosk"
          ) : (
            "Start New Session & Open Kiosk"
          )}
        </Button>

        <p className="text-xs text-slate-500 mt-3">
          Starting a new session will end any other active session you own.
        </p>

        {/* SMTP diagnostic — verify production email delivery without curling endpoints */}
        <div className="mt-5 pt-4 border-t border-slate-700">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Email diagnostic
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Sends a test email to{" "}
                <span className="text-slate-300 font-mono">{ADMIN_TEST_EMAIL}</span> and shows the result.
              </div>
            </div>
            <Button
              onClick={handleSendTestEmail}
              disabled={testSending}
              data-testid="kiosk-test-email-btn"
              variant="outline"
              className="shrink-0 border-slate-600 bg-[#1B3A6B] hover:bg-[#264a85] text-white font-semibold"
            >
              {testSending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Test Email to Admin
                </>
              )}
            </Button>
          </div>

          {testResult && testResult.success && (
            <div
              className="mt-3 rounded-lg border border-teal-700/60 bg-teal-900/25 p-3 text-sm transition-opacity"
              data-testid="kiosk-test-email-result-success"
            >
              <div className="flex items-start gap-2 text-teal-300 font-semibold">
                <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#00A896" }} />
                <span>Email sent successfully via {testResult.transport}</span>
              </div>
              <div className="mt-1 ml-7 text-slate-300">
                Check <span className="font-mono">{ADMIN_TEST_EMAIL}</span> inbox to confirm delivery.
              </div>
            </div>
          )}

          {testResult && !testResult.success && (
            <div
              className="mt-3 rounded-lg border border-red-700/60 bg-red-900/25 p-3 text-sm transition-opacity"
              data-testid="kiosk-test-email-result-failure"
            >
              <div className="flex items-start gap-2 text-red-300 font-semibold">
                <XCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>Email send failed</span>
              </div>
              <div className="mt-1 ml-7 text-slate-300 break-words">
                {(testResult.error || "Unknown error").length > 200
                  ? `${(testResult.error || "").slice(0, 200)}…`
                  : (testResult.error || "Unknown error")}
              </div>
              <a
                href="/api/kiosk/email-diagnostic"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 ml-7 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-teal-400"
                data-testid="kiosk-test-email-diagnostic-link"
              >
                View full diagnostic <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default KioskLauncherCard;
